import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import type {KeyboardEvent, ReactNode} from "react";
import "./DocumentSummaryLine.css";

type Props = {
  icon: ReactNode;
  iconClassName?: string;
  title: ReactNode;
  subtitle: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
};

/**
 * Ligne de résumé de document (icône, titre, sous-titre, slot trailing) —
 * réutilisable pour tout document lié à un rendez-vous ou un client
 * (attestation, compte-rendu, rappel...). Sans `onClick`, la ligne est
 * statique ; avec, toute la ligne devient l'affordance de clic (pas de
 * bouton séparé), chevron par défaut si `trailing` n'est pas fourni.
 */
export function DocumentSummaryLine({
  icon,
  iconClassName,
  title,
  subtitle,
  trailing,
  onClick,
  disabled = false,
}: Props) {
  const clickable = Boolean(onClick);
  const resolvedTrailing =
    trailing ??
    (clickable ? (
      <FontAwesomeIcon icon="chevron-right" className="document-summary-line-chevron" />
    ) : null);

  return (
    <div
      className={`document-summary-line${clickable ? " clickable" : ""}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-disabled={clickable ? disabled : undefined}
      onClick={clickable && !disabled ? onClick : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
    >
      <span className={`document-summary-line-icon${iconClassName ? ` ${iconClassName}` : ""}`}>
        {icon}
      </span>
      <div className="document-summary-line-text">
        <div className="document-summary-line-title">{title}</div>
        <div className="document-summary-line-subtitle">{subtitle}</div>
      </div>
      {resolvedTrailing}
    </div>
  );

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if ((e.key === "Enter" || e.key === " ") && !disabled && onClick) {
      e.preventDefault();
      onClick();
    }
  }
}
